import React, { useEffect, useRef, useState, useContext } from "react";
import ReactDOM from "react-dom/client";
import Grapick from "./grapick/Grapick";
import "./gradient-v2.scss";
import SelectInput from "./select";
import AngleInput from "./angle";
import { Popover, PopoverAnchor, PopoverContent } from ".";
import GlobalSketchPicker from "./globalsketchpicker";
import { GrigoraDashboardContext } from "../dashboard/context";
import { throttle } from "lodash";

const ColorPicker = ({ value, onChange, project }) => {
  const [openPopOver, setOpenPopOver] = useState(false);
  const [innerVal, setinnerVal] = useState(value);

  const handleColorChange = (e) => {
    onChange(e);
    setinnerVal(e);
  };
  return (
    <>
      <Popover
        placement="left"
        open={openPopOver}
        onOpenChange={setOpenPopOver}
      >
        <PopoverAnchor>
          <div
            className="w-[15px] h-[15px] ml-[-5px] rounded-full cursor-pointer"
            style={{
              backgroundColor: innerVal ? innerVal : "transparent",
              boxShadow: "inset 0 0 0 1px rgb(255 255 255 / 20%)",
            }}
            onClick={() => {
              setOpenPopOver(true);
            }}
          ></div>
        </PopoverAnchor>
        <PopoverContent className="!z-[1000] !rounded-[6px] !shadow-editor-floating-elements">
          <div className=" p-4">
            <GlobalSketchPicker
              value={innerVal}
              onChange={handleColorChange}
              explicitProject={project}
            />
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default function GradientPicker({
  /** Start opting into the new margin-free styles that will become the default in a future version. */
  className,
  gradients,
  onChange,
  value,
  resetValue,
  colorPicker,
}) {
  const { project } = useContext(GrigoraDashboardContext) ?? {};
  const [type, setType] = useState("");
  const [direction, setDirection] = useState(0);

  const grapickRef = useRef();

  const _gp = useRef(undefined);

  useEffect(() => {
    return () => {
      if (_gp.current) {
        _gp.current.off("change", throttledOnGPChange);
        _gp.current.destroy();
        _gp.current = 0;
      }
    };
  }, []);

  const onGPChange = (complete) => {
    let newVal = _gp.current && _gp.current.getValue();
    if (newVal) {
      onChange(newVal);
    }
  };

  const throttledOnGPChange = throttle(onGPChange, 400);

  useEffect(() => {
    _gp.current = new Grapick({
      el: grapickRef.current,
      colorEl: colorPicker === "sketch" ? '<div id="colorpicker"/>' : undefined,
    });

    if (colorPicker === "sketch") {
      _gp.current.setColorPicker((handler) => {
        const el = handler.getEl().querySelector("#colorpicker");

        const root = ReactDOM.createRoot(el);

        root.render(
          <React.StrictMode>
            <ColorPicker
              value={handler.getColor()}
              onChange={(val) => {
                handler.setColor(val);
              }}
              project={project}
            />
          </React.StrictMode>,
        );

        // return a function in order to destroy the custom color picker
        return () => {
          // Todo check if gradient color input mount causes issues.
        };
      });
    }

    if (value !== "" || value !== null) {
      _gp.current.setValue(value); //value is set
      var grtype = _gp.current.getType();
      var grdirection = _gp.current.getDirection();
      setType(grtype);
      setDirection(grdirection);
    } else {
      // Handlers are color stops
      _gp.current.addHandler(0, "red");
      _gp.current.addHandler(100, "blue");
    }

    // Do stuff on change of the gradient
    _gp.current.on("change", throttledOnGPChange);
  }, [grapickRef]);

  return (
    <div className="">
      <div ref={grapickRef} className="mb-6 mt-6"></div>
      <div className="flex flex-row gap-[5px]">
        <SelectInput
          isResetVisible={false}
          className="flex-1"
          value={type}
          options={[
            {
              label: "Linear",
              value: "linear",
            },
            {
              label: "Radial",
              value: "radial",
            },
          ]}
          onChange={(e) => {
            setType(e);
            if (e === "radial") {
              setDirection("center");
              _gp.current && _gp.current.setType(e || "linear");
              _gp.current && _gp.current.setDirection("center");
            } else {
              setDirection(0);
              _gp.current && _gp.current.setType(e || "linear");
              _gp.current && _gp.current.setDirection(`0deg`);
            }
          }}
        />
        {type !== "radial" ? (
          <>
            <AngleInput
              isResetVisible={false}
              className="flex-1"
              value={direction}
              onChange={(e) => {
                if (e !== undefined && e !== null) {
                  setDirection(e);
                  _gp.current && _gp.current.setDirection(`${e}deg`);
                } else {
                  _gp.current && _gp.current.setDirection("right");
                }
              }}
            />
          </>
        ) : (
          <>
            <SelectInput
              isResetVisible={false}
              className="flex-1"
              value={direction}
              options={[
                {
                  label: "Top",
                  value: "circle at top",
                },
                {
                  label: "Right",
                  value: "circle at right",
                },
                {
                  label: "Center",
                  value: "circle at center",
                },
                {
                  label: "Bottom",
                  value: "circle at bottom",
                },
                {
                  label: "Left",
                  value: "circle at left",
                },
              ]}
              onChange={(e) => {
                if (e) {
                  setDirection(e);
                  _gp.current && _gp.current.setDirection(e);
                } else {
                  _gp.current && _gp.current.setDirection("right");
                }
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
